<script setup lang="ts">
import { Head, Link, router, usePage } from '@inertiajs/vue3'
import { Pencil, Plus, Search, Trash2, Users2, X } from 'lucide-vue-next'
import { computed, onUnmounted, ref, watch } from 'vue'

import AppDataTable from '@/components/AppDataTable.vue'
import type { TableColumn } from '@/components/AppDataTable.vue'
import AppPagination from '@/components/AppPagination.vue'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useDialog } from '@/composables/useDialog'
import AppLayout from '@/layouts/AppLayout.vue'
import { editUserPath, newUserPath, userPath, usersPath } from '@/routes'
import { type BreadcrumbItem } from '@/types'

interface User {
  id: number
  name: string
  email: string
  active: boolean
  roles: string[]
  created_at: string
}

interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Filters {
  q?: string | null
  active?: string | null
}

const props = defineProps<{
  users: User[]
  pagination: PaginationMeta
  filters: Filters
}>()

const breadcrumbs: BreadcrumbItem[] = [{ title: 'Staff', href: usersPath() }]

const isAdmin = computed(() => (usePage().props.auth as { roles: string[] }).roles.includes('admin'))

const ALL = '__all__'

const search = ref(props.filters.q ?? '')
const activeFilter = ref(props.filters.active ?? ALL)

const hasActiveFilters = computed(() => search.value !== '' || activeFilter.value !== ALL)

const filterParams = computed(() => ({
  q: search.value || undefined,
  active: activeFilter.value !== ALL ? activeFilter.value : undefined,
}))

let searchTimer: ReturnType<typeof setTimeout>

function applyFilters() {
  const params = Object.fromEntries(Object.entries(filterParams.value).filter(([, v]) => v != null))
  router.get(usersPath(), params, { preserveState: true, replace: true })
}

onUnmounted(() => clearTimeout(searchTimer))

watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(applyFilters, 350)
})

watch(activeFilter, applyFilters)

function clearFilters() {
  search.value = ''
  activeFilter.value = ALL
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  manager: 'Manager',
  associate: 'Associate',
}

const ROLE_VARIANTS: Record<string, 'default' | 'secondary' | 'outline'> = {
  admin: 'default',
  manager: 'secondary',
  associate: 'outline',
}

const tableColumns: TableColumn[] = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'role', label: 'Role' },
  { key: 'status', label: 'Status' },
  { key: 'actions', label: 'Actions', align: 'right' },
]

// ── Delete ────────────────────────────────────────────────────────────────
const { confirm } = useDialog()

async function confirmDelete(user: User) {
  const ok = await confirm({
    title: 'Delete user?',
    description: `Are you sure you want to delete ${user.name}? This action cannot be undone.`,
    confirmLabel: 'Delete',
  })
  if (!ok) return
  router.delete(userPath(user.id), { preserveScroll: true })
}
</script>

<template>
  <Head title="Staff" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="flex h-full flex-1 flex-col gap-4 overflow-x-auto p-4">
      <!-- Page header -->
      <div class="flex items-center gap-3">
        <Users2 class="text-muted-foreground size-6" />
        <div>
          <h1 class="text-foreground text-xl font-semibold">Staff</h1>
          <p class="text-muted-foreground text-sm">Manage internal staff accounts</p>
        </div>
      </div>

      <!-- Table card -->
      <Card class="border-sidebar-border/70 dark:border-sidebar-border">
        <CardHeader class="pb-3">
          <div class="flex items-center justify-between">
            <div>
              <CardTitle class="text-base">All Staff</CardTitle>
              <CardDescription>
                {{ pagination.total_count }} user{{ pagination.total_count !== 1 ? 's' : '' }}{{ hasActiveFilters ? ' found' : ' total' }}
              </CardDescription>
            </div>
            <Button v-if="isAdmin" size="sm" as-child>
              <Link :href="newUserPath()">
                <Plus class="size-4" />
                Add Staff
              </Link>
            </Button>
          </div>
        </CardHeader>

        <!-- Search & filters -->
        <div class="border-border border-b px-4 pb-4">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div class="relative flex-1">
              <Search class="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input v-model="search" placeholder="Search by name or email…" class="pl-9" />
            </div>

            <Select v-model="activeFilter">
              <SelectTrigger class="w-full sm:w-36">
                <SelectValue placeholder="Any status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem :value="ALL">Any status</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>

            <Button
              v-if="hasActiveFilters"
              variant="ghost"
              size="sm"
              class="text-muted-foreground hover:text-foreground shrink-0"
              @click="clearFilters"
            >
              <X class="size-4" />
              Clear
            </Button>
          </div>
        </div>

        <CardContent class="p-0">
          <!-- Empty state -->
          <div v-if="users.length === 0" class="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <Users2 class="text-muted-foreground/40 size-12" />
            <p class="text-muted-foreground text-sm">
              {{ hasActiveFilters ? 'No staff match your filters' : 'No staff accounts yet' }}
            </p>
            <Button v-if="hasActiveFilters" variant="outline" size="sm" @click="clearFilters">
              <X class="size-4" />
              Clear filters
            </Button>
          </div>

          <!-- Table -->
          <AppDataTable v-else :columns="tableColumns">
            <tr
              v-for="user in users"
              :key="user.id"
              class="border-border hover:bg-muted/50 border-b transition-colors last:border-0"
            >
              <td class="px-6 py-4 font-medium">
                <Link :href="editUserPath(user.id)" class="text-foreground hover:text-primary hover:underline">
                  {{ user.name }}
                </Link>
              </td>
              <td class="text-muted-foreground px-6 py-4">{{ user.email }}</td>
              <td class="px-6 py-4">
                <div class="flex flex-wrap gap-1">
                  <Badge v-for="role in user.roles" :key="role" :variant="ROLE_VARIANTS[role] ?? 'outline'">
                    {{ ROLE_LABELS[role] ?? role }}
                  </Badge>
                  <span v-if="user.roles.length === 0" class="text-muted-foreground/50 text-xs">—</span>
                </div>
              </td>
              <td class="px-6 py-4">
                <Badge :variant="user.active ? 'default' : 'secondary'">
                  {{ user.active ? 'Active' : 'Inactive' }}
                </Badge>
              </td>
              <td class="px-6 py-4">
                <div class="flex items-center justify-end gap-2">
                  <Button variant="ghost" size="icon" class="size-8" as-child>
                    <Link :href="editUserPath(user.id)">
                      <Pencil class="size-4" />
                      <span class="sr-only">Edit</span>
                    </Link>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="text-destructive hover:text-destructive size-8"
                    @click="confirmDelete(user)"
                  >
                    <Trash2 class="size-4" />
                    <span class="sr-only">Delete</span>
                  </Button>
                </div>
              </td>
            </tr>
          </AppDataTable>

          <!-- Pagination -->
          <AppPagination
            v-if="pagination.total_pages > 1"
            :current-page="pagination.current_page"
            :total-pages="pagination.total_pages"
            :total-count="pagination.total_count"
            :per-page="pagination.per_page"
            :base-url="usersPath()"
            :extra-params="filterParams"
          />
        </CardContent>
      </Card>
    </div>

  </AppLayout>
</template>
